import moment from 'moment-timezone';
import { percentageToRatio, ratioToPercentage } from './percentage';

export const WEEK_DAY_OPTIONS = Object.freeze([
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
]);

function numberOrNull(value) {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function metadataDefault(metadata, key) {
  return numberOrNull(metadata && metadata.defaults && metadata.defaults[key]);
}

export function createDefaultScheduleFormState(metadata) {
  return {
    frequency: 'daily',
    dayOfWeek: 1,
    time: null,
    lookbackAmount: null,
    lookbackUnit: null,
    thresholdAlert: metadataDefault(metadata, 'threshold_alert'),
    frequencyThreshold: ratioToPercentage(
      metadataDefault(metadata, 'frequency_threshold')
    ),
    contactGroupId: null
  };
}

export function parseScheduledTime(value) {
  if (!value) return null;
  const time = moment(value, ['HH:mm:ss', 'HH:mm'], true);
  return time.isValid() ? time : null;
}

export function mapRecurringScheduleRecord(record, metadata) {
  const draft = createDefaultScheduleFormState(metadata);
  return {
    ...draft,
    frequency: record && record.frequency === 'weekly' ? 'weekly' : 'daily',
    dayOfWeek:
      record && record.day_of_week != null ? Number(record.day_of_week) : 1,
    time: parseScheduledTime(record && record.scheduled_time),
    lookbackAmount:
      record && record.lookback_amount != null
        ? Number(record.lookback_amount)
        : null,
    lookbackUnit: (record && record.lookback_unit) || null,
    thresholdAlert:
      record && record.threshold_alert != null
        ? Number(record.threshold_alert)
        : draft.thresholdAlert,
    frequencyThreshold:
      record && record.frequency_threshold != null
        ? ratioToPercentage(record.frequency_threshold)
        : draft.frequencyThreshold,
    contactGroupId:
      record && record.contact_group_id != null
        ? record.contact_group_id
        : null
  };
}

export function getLookbackUnitLimits(metadata, unit) {
  const lookback =
    metadata && metadata.limits && metadata.limits.lookback
      ? metadata.limits.lookback
      : {};
  return lookback[unit] || null;
}

export function validateAutoFinetuneThresholds(draft, metadata) {
  const thresholdAlert = numberOrNull(draft && draft.thresholdAlert);
  const thresholdAlertLimits =
    metadata && metadata.limits && metadata.limits.threshold_alert;
  if (!thresholdAlertLimits) {
    return 'Alert threshold limits are unavailable.';
  }
  const thresholdAlertMinimum = Number(thresholdAlertLimits.min);
  if (
    thresholdAlert == null ||
    !Number.isInteger(thresholdAlert) ||
    thresholdAlert < thresholdAlertMinimum
  ) {
    return `Alert Threshold must be a whole number of ${thresholdAlertMinimum} or greater.`;
  }

  const frequencyThreshold = numberOrNull(draft && draft.frequencyThreshold);
  const frequencyLimits =
    metadata &&
    metadata.limits &&
    metadata.limits.frequency_threshold_percent;
  if (!frequencyLimits) {
    return 'Frequency threshold limits are unavailable.';
  }
  const minimum = Number(frequencyLimits.min);
  const maximum = Number(frequencyLimits.max);
  if (
    frequencyThreshold == null ||
    frequencyThreshold < minimum ||
    frequencyThreshold > maximum
  ) {
    return `Frequency threshold must be between ${minimum}% and ${maximum}%.`;
  }
  return '';
}

export function validateRecurringScheduleDraft(draft, metadata) {
  if (!metadata) return 'Schedule metadata is unavailable.';
  if (!draft || !['daily', 'weekly'].includes(draft.frequency)) {
    return 'Please select a valid frequency.';
  }
  if (!draft.time || !moment(draft.time).isValid()) {
    return 'Please select a valid time.';
  }
  if (
    draft.frequency === 'weekly' &&
    (!Number.isInteger(Number(draft.dayOfWeek)) ||
      Number(draft.dayOfWeek) < 0 ||
      Number(draft.dayOfWeek) > 6)
  ) {
    return 'Please select a day of week.';
  }
  if (!getLookbackUnitLimits(metadata, draft.lookbackUnit)) {
    return 'Please select a lookback unit.';
  }

  const amount = numberOrNull(draft.lookbackAmount);
  const unitLimits = getLookbackUnitLimits(metadata, draft.lookbackUnit);
  if (!unitLimits) return 'Lookback limits are unavailable.';
  const minimum = Number(unitLimits.min);
  const maximum = Number(unitLimits.max);
  if (
    amount == null ||
    !Number.isInteger(amount) ||
    amount < minimum ||
    amount > maximum
  ) {
    return `Lookback amount must be a whole number between ${minimum} and ${maximum}.`;
  }

  const maximumSeconds = Number(
    metadata && metadata.limits && metadata.limits.max_lookback_seconds
  );
  if (
    Number.isFinite(maximumSeconds) &&
    amount * Number(unitLimits.seconds) > maximumSeconds
  ) {
    return 'Lookback exceeds the maximum analysis window.';
  }

  return validateAutoFinetuneThresholds(draft, metadata);
}

export function buildRecurringSchedulePayload(draft) {
  return {
    frequency: draft.frequency,
    day_of_week:
      draft.frequency === 'weekly' ? Number(draft.dayOfWeek) : null,
    scheduled_time: moment(draft.time).format('HH:mm:ss'),
    lookback_amount: Number(draft.lookbackAmount),
    lookback_unit: draft.lookbackUnit,
    threshold_alert: Number(draft.thresholdAlert),
    frequency_threshold: percentageToRatio(draft.frequencyThreshold),
    contact_group_id:
      draft.contactGroupId == null ? null : Number(draft.contactGroupId)
  };
}

export function formatRecurringSchedule(record) {
  const time = parseScheduledTime(record && record.scheduled_time);
  const timeLabel = time ? time.format('HH:mm:ss') : '—';
  if (record && record.frequency === 'weekly') {
    const day = WEEK_DAY_OPTIONS.find(
      (option) => option.value === Number(record.day_of_week)
    );
    return `Every ${day ? day.label : 'week'} at ${timeLabel}`;
  }
  return `Every day at ${timeLabel}`;
}

export function formatScheduleLookback(record) {
  if (!record || record.lookback_amount == null || !record.lookback_unit) {
    return '—';
  }
  const amount = Number(record.lookback_amount);
  const unit =
    amount === 1
      ? record.lookback_unit.replace(/s$/, '')
      : record.lookback_unit;
  return `${amount} ${unit}`;
}
